import { useProjectParams } from '@hooks/useProjectParams';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimContractData } from 'gql/graphql';
import { ProjectContractTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { ContractUpdateToolbar } from 'pages/Project/Common/Contract/ContractUpdateToolbar/ContractUpdateToolbar';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { notFrameLowName } from 'pages/Project/Common/types';
import type { AnyObject } from 'types/AnyObject';
import { Loader } from 'uikit';

import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useBimContract } from '../hooks/useBimContract';
import { useBimContractDefaults } from '../hooks/useBimContractDefaults';

import { BimContractEditForm } from './components/BimContractEditForm';
import { useIsDisabledByStatus } from './hooks/useIsDisabledByStatus';
import { initialPriceParametersToInput, useSaveContract } from './hooks/useSaveContract';

type FormRef = typeof BimContractEditForm & { seasonsAllYear: AnyObject[] };

interface IBimContractEdit {
    isCreate?: boolean;
}

export const BimContractEdit: React.FC<IBimContractEdit> = (props) => {
    const { isCreate = false } = props;
    const navigate = useNavigate();
    const formRef = useRef<FormRef>(null);
    const { id: projectId, methodology } = useProjectParams();

    const { projectData, projectDataStatus, refreshProject } = useProjectData(projectId);

    const actualContractId = useMemo(() => projectData?.contractId, [projectData?.contractId]);

    const isEdit = !!actualContractId;
    const title = isEdit ? 'Редактирование договора' : 'Создание договора';

    const { bimContractData, bimContractDataStatus, makeResetBimContract } = useBimContract(actualContractId, isEdit);

    const { bimContractDefaultsData, bimContractDefaultsStatus, bimContractDefaultsMakeReset } = useBimContractDefaults(
        projectId,
        actualContractId,
    );

    const isFrameContract = useMemo(
        () => projectData?.contractType === ProjectContractTypeEnum.Frame,
        [projectData?.contractType],
    );

    const isFrameContractWithWorkOrders = useMemo(
        () => isFrameContract && !!projectData?.contractNavigation?.data?.workOrderNavs?.length,
        [isFrameContract, projectData?.contractNavigation?.data?.workOrderNavs?.length],
    );

    const contractCalculation = useMemo(
        () =>
            !isFrameContract
                ? projectData?.contractNavigation?.data?.contractNavItems?.find(
                      (n) => n.name.toLowerCase() === notFrameLowName,
                  )
                : undefined,
        [isFrameContract, projectData?.contractNavigation?.data?.contractNavItems],
    );

    const contractCalculationStatus: WorkOrderStatusEnum | undefined = useMemo(
        () => contractCalculation?.metadata?.status,
        [contractCalculation?.metadata?.status],
    );

    const isNormalContractWithCalculation = useMemo(
        () =>
            !isFrameContract &&
            !!projectData?.contractNavigation?.data?.contractNavItems?.find(
                (n) => n.name.toLowerCase() === notFrameLowName,
            ),
        [isFrameContract, projectData?.contractNavigation?.data?.contractNavItems],
    );

    const { data, status, reset, error, makeRequest, isSubmitted, onCreateReportDownload } = useSaveContract({
        projectId,
        contractId: actualContractId,
    });

    useEffect(() => {
        if (status === RequestStatus.SUCCESS && data?.result === true) {
            refreshProject();
            reset();
            makeResetBimContract(null);
            bimContractDefaultsMakeReset(null);
            navigate(`/projects/${methodology}/${projectId}/contract`);
        }
    }, [
        bimContractDefaultsMakeReset,
        data?.result,
        makeResetBimContract,
        methodology,
        navigate,
        projectId,
        refreshProject,
        reset,
        status,
    ]);

    useEffect(
        () => () => {
            makeResetBimContract(null);
        },
        [makeResetBimContract],
    );

    const isDisabledByStatusMap = useIsDisabledByStatus(bimContractData?.status);

    const hasWorkOrders = useMemo(
        () =>
            isFrameContract
                ? !!projectData?.contractNavigation?.data?.workOrderNavs?.length
                : !!projectData?.contractNavigation?.data?.contractNavItems?.find(
                      (n) => n.name.toLowerCase() === notFrameLowName,
                  ),
        [
            isFrameContract,
            projectData?.contractNavigation?.data?.contractNavItems,
            projectData?.contractNavigation?.data?.workOrderNavs?.length,
        ],
    );

    const hasIncompleteWorkOrders = useMemo(
        () =>
            isFrameContract
                ? !!projectData?.contractNavigation?.data?.workOrderNavs?.find(
                      (n) => n.status !== WorkOrderStatusEnum.Completed,
                  )
                : !!projectData?.contractNavigation?.data?.contractNavItems?.find(
                      (n) =>
                          n.name.toLowerCase() === notFrameLowName &&
                          n.metadata.status !== WorkOrderStatusEnum.Completed,
                  ),
        [
            isFrameContract,
            projectData?.contractNavigation?.data?.contractNavItems,
            projectData?.contractNavigation?.data?.workOrderNavs,
        ],
    );

    const hasWorkOrderChangeRequired = useMemo(
        () =>
            isFrameContract &&
            !!projectData?.contractNavigation?.data?.workOrderNavs?.find((n) => n.metadata?.isWorkOrderChangeRequired),
        [isFrameContract, projectData?.contractNavigation?.data?.workOrderNavs],
    );

    const isLoading = useMemo(
        () =>
            projectDataStatus === RequestStatus.FETCHING ||
            bimContractDataStatus === RequestStatus.FETCHING ||
            bimContractDefaultsStatus === RequestStatus.FETCHING,
        [bimContractDataStatus, bimContractDefaultsStatus, projectDataStatus],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
        ],
        [methodology, projectData?.name, projectId],
    );

    const onSubmit = useCallback(
        (formData: any) => {
            const priceParameters =
                hasWorkOrders || isDisabledByStatusMap.priceForm
                    ? initialPriceParametersToInput(bimContractData)
                    : null;

            makeRequest(formData, priceParameters);
        },
        [bimContractData, hasWorkOrders, isDisabledByStatusMap, makeRequest],
    );

    return (
        <ProjectPageLayout
            isHeaderCollapsable
            title={title}
            breadCrumbsItems={breadCrumbsItems}
            headerBottom={
                <div className="tool-bar">
                    <ContractUpdateToolbar
                        formRef={formRef}
                        isLoading={status === RequestStatus.FETCHING}
                        isDisabledCancel={status === RequestStatus.FETCHING}
                        isCreate={isCreate}
                        resetData={makeResetBimContract}
                        statusProject={projectData?.status}
                    />
                </div>
            }
        >
            {isLoading && <Loader />}
            {!isLoading && (
                <BimContractEditForm
                    onSubmit={onSubmit}
                    initialData={(bimContractData || bimContractDefaultsData) as BimContractData}
                    ref={formRef}
                    isCreate={isCreate}
                    hasWorkOrders={hasWorkOrders}
                    hasIncompleteWorkOrders={hasIncompleteWorkOrders}
                    hasWorkOrderChangeRequired={hasWorkOrderChangeRequired}
                    isDisabledByStatusMap={isDisabledByStatusMap}
                    isFrameContract={isFrameContract}
                    contractType={projectData?.contractType as ProjectContractTypeEnum}
                    isFrameContractWithWorkOrders={isFrameContractWithWorkOrders}
                    isNormalContractWithCalculation={isNormalContractWithCalculation}
                    contractId={actualContractId}
                    statusProject={projectData?.status}
                    contractCalculationStatus={contractCalculationStatus}
                />
            )}
            <ErrorHandlingModal
                titleText={title}
                isSubmitted={isSubmitted}
                statusRequest={status}
                dataRequest={data}
                errorsRequest={error}
                handleCloseModalError={reset}
                handleCreateReport={onCreateReportDownload}
            />
        </ProjectPageLayout>
    );
};

BimContractEdit.displayName = 'BimContractEdit';
